export * from './photoProducts'
export * from './photoProductsCart'

export type CuratedPhoto = {
  height: number
  tookAt: string
  url: string
  uuid: string
  width: number
}

export type CuratedPhotoForPhotoProducts = {
  mediumId: string
  croppedUrl: string
  url: string
  x: number
  y: number
  width: number
  height: number
}

export type CropDefinition = {
  x: number
  y: number
  width: number
  height: number
}

export enum VariantCellPositionRelativeToTemplate {
  OnTop = 'OnTop',
  Beneath = 'Beneath',
}

export enum CellType {
  Image = 'Image',
  PatternImage = 'PatternImage',
  Text = 'Text',
}

export type TextData = {
  id: number
  fontFamily: string
  fontWeight: number
  fontSize: number
  fontColor: string
  textAlign: string
  lineHeight: number
  charLimit: number
  placeholder: string
  expression: string | null
  rotationAngle: number
  autoScaleFromVariable?: {
    key: string
    minSize: number
    maxChars: number
  } | null
}

export type Cell = {
  id: number
  absoluteCenterX: number
  absoluteCenterY: number
  width: number
  height: number
  order: number
  zIndex: number
  positionRelativeToTemplate: VariantCellPositionRelativeToTemplate
  type: CellType.Image | CellType.PatternImage | CellType.Text
  imageData: { id: number; rotationAngle: number } | null
  textData: TextData | null
  templateVariantId: number
  patternData: {
    matrix: number[][]
  } | null
  shape?: 'rectangle' | 'circle'
}

export type ColorOption = {
  label: string
  value: string
  maskImageUrl: string
}

export type TemplateOptionChoice = {
  label: string
  maskImageUrl: string
  hex?: string
  /**
   * Filename slug for this choice — referenced when the parent template has a
   * `maskImageUrlPattern` and the active mask is composed from multiple
   * templateOption selections. Optional for single-option templates.
   */
  slug?: string
}

export type TemplateOption =
  | { type: 'color'; label: string; choices: (TemplateOptionChoice & { hex: string })[]; requiresExplicitSelection?: boolean }
  | { type: 'select'; label: string; choices: TemplateOptionChoice[]; requiresExplicitSelection?: boolean }

export type CoverOption = {
  value: string
  label: string
  maskImageUrl: string
  thumbnailUrl?: string
  isDefault?: boolean
  spreadOverrides?: {
    spreadOrder: number
    maskImageUrl?: string
    textColor?: string
    textCellIds?: number[]
  }[]
}

export type TextGroup = {
  id: string
  container: {
    x: number
    y: number
    width: number
    height: number
  }
  memberCellIds: number[]
  gap?: number
  verticalAlign?: 'center' | 'top'
  overflow?: 'hidden' | 'visible'
}

export type TemplateVariable = {
  key: string
  label: string
  placeholder?: string | null
  defaultValue: string
  charLimit: number
  enableAutoPrefillFromPrevious?: boolean | null
  modifiers?: 'uppercase'[] | null
}

export type DesignTemplate = {
  id: number
  name: string
  productVariantIds: number[]
  maskImageUrl: string
  previewMaskUrl?: string
  productionMaskUrl?: string
  cropStencilClassName?: string
  stencilImageUrl?: string
  numberOfUserImages: number
  cells: Cell[]
  thumbnailUrl: string | string[]
  colorOptions?: ColorOption[]
  coverOptions?: CoverOption[] | null
  textGroups?: TextGroup[] | null
  variables?: TemplateVariable[] | null
  textData?: TextData | null
  isFlippable?: boolean
  templateOptions?: TemplateOption[] | null
  /**
   * Optional printf-style pattern (e.g. `printify/coffee/coffee_11_{0}_{1}.png`)
   * for composing the active mask URL from multiple `templateOptions` selections.
   * Placeholders are replaced by the `slug` of each option's selected choice in
   * order. When unset, the active mask follows the legacy first-option-only
   * behavior.
   */
  maskImageUrlPattern?: string | null
}

// Storybook template types

export type StorybookSpread = {
  id: number
  spreadOrder: number
  spreadType: 'COVER' | 'INTERIOR'
  spreadLabel: string | null
  printAreaPosition: string
  skipInEditorFlow?: boolean
  imageBindings?:
    | {
        targetCellId: number
        sourceSpreadId: number
        sourceCellId: number
      }[]
    | null
  templateVariant: DesignTemplate
}

export type StorybookTemplateSummary = {
  id: number
  name: string
  productVariantIds: number[]
  thumbnailUrl: string
  isActive: boolean
}

export type StorybookTemplate = StorybookTemplateSummary & {
  spreads: StorybookSpread[]
}
